import { useId, type ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export interface FieldRenderProps {
  /** Put this on the control. The label's `htmlFor` already points at it. */
  id: string;
  /** Space-separated ids of the hint and/or error text. Put on `aria-describedby`. */
  describedBy: string | undefined;
  /** Put on `aria-invalid`. */
  invalid: boolean;
  required: boolean;
}

export interface FieldProps {
  /** Required. There is no unlabelled control in this app — see the note below. */
  label: string;
  /** Persistent helper text. Rendered before the error so both can coexist. */
  hint?: ReactNode;
  /** Validation message. Presence of this flips the control into its error state. */
  error?: string | undefined;
  required?: boolean;
  /** Pass to control the id (e.g. to match a react-hook-form field name). */
  id?: string;
  className?: string;
  /** Visually hides the label while keeping it in the accessibility tree. */
  labelHidden?: boolean;
  children: (props: FieldRenderProps) => ReactNode;
}

/**
 * The labelling and description plumbing every form control shares.
 *
 * `label` is a required prop, not an optional one, and it is rendered as a real
 * `<label for>` — not a placeholder, not an aria-label. Placeholders disappear the
 * moment a user types, which is exactly when they need to check what the field
 * was; and a floating-label-only design leaves nothing for a screen reader to
 * announce when the field is pre-filled.
 *
 * Errors are wired through `aria-describedby` AND given `role="alert"`, so the
 * message is both discoverable when tabbing into a bad field and announced when
 * it first appears.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  id,
  className,
  labelHidden = false,
  children,
}: FieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={fieldId}
        className={cn('text-sm font-medium text-ink', labelHidden && 'sr-only')}
      >
        {label}
        {required ? (
          <>
            {' '}
            <span className="text-danger" aria-hidden="true">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        ) : null}
      </label>

      {children({ id: fieldId, describedBy, invalid: Boolean(error), required })}

      {hint ? (
        <p id={hintId} className="text-sm text-ink-subtle">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="flex gap-1.5 text-sm font-medium text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
